import {createSelector} from '@reduxjs/toolkit';

import {ApplicationStoreType} from 'desktop/store';
import {AddressType, FormattedAddressType} from 'desktop/types/addressType';

import {MODULE_KEY_CHECKOUT} from '../constants';

/**
 * Get list of customer shipping addresses
 *
 * @param {ApplicationStoreType} store - current store
 * @return {AddressType} list of shipping addresses
 */
export const getShippingAddress = (store: ApplicationStoreType): AddressType =>
    store[MODULE_KEY_CHECKOUT].deliveryStep.shippingAddress;

/**
 * Returns shipping address country
 *
 * @param {ApplicationStoreType} store - current store
 * @return {string} shipping country
 */
export const getShippingCountry = createSelector(
    getShippingAddress,
    shippingAddress => shippingAddress.country.value,
);

/**
 * Returns customer email
 *
 * @param {ApplicationStoreType} store - current store
 * @return {string} customer email
 */
export const getCustomerEmail = createSelector(
    getShippingAddress,
    customer => customer.email.value,
);

/**
 * Returns shipping address state
 *
 * @param {ApplicationStoreType} store - current store
 * @return {string} currently selected state
 */
export const getShippingAddressStateName = createSelector(
    getShippingAddress,
    (shippingAddress: AddressType): string => shippingAddress.state.value,
);

/**
 * Returns formatted address
 *
 * @param {ApplicationStoreType} store - current store
 * @return {FormattedAddressType} formatted address
 */
export const getFormattedAddress = createSelector(
    getShippingAddress,
    (address: AddressType): FormattedAddressType => ({
        address: address.other?.value
            ? `${address.address?.value || ''} ${address.other.value}`
            : address.address?.value || '',
        city: address.city.value,
        code: address.postal?.value || '',
        country: address.country.value,
        email: address.email.value,
        name: `${address.firstName.value} ${address.lastName.value}`,
        phone: address.phone.value,
        state: address.state?.value || '',
    }),
);
